// Mengeneinheiten — Gegenstück zu backend/lib/einheiten.js.
//
// Beide Listen müssen dieselben Einträge haben; der Backend-Test
// test/einheiten.unit.test.mjs prüft das bei jedem Lauf.
//
// Gespeichert und gedruckt wird immer der deutsche Name (`name`). In der
// Auswahl erscheint er übersetzt — Symbole wie kg oder m² sind international
// und bleiben, wie sie sind.
import type { Schluessel } from '../../../gemeinsam/lib/i18n';

export type Groesse = 'anzahl' | 'gebinde' | 'gewicht' | 'volumen' | 'laenge' | 'flaeche' | 'zeit' | 'energie' | 'pauschal';

export type Einheit = { name: string; code: string; groesse: Groesse; faktor?: number; schluessel?: Schluessel };

export const EINHEITEN: Einheit[] = [
  { name: 'Stück',     code: 'H87', groesse: 'anzahl',  schluessel: 'einheit.stueck' },
  { name: 'Paar',      code: 'PR',  groesse: 'gebinde', schluessel: 'einheit.paar' },
  { name: 'Satz',      code: 'SET', groesse: 'gebinde', schluessel: 'einheit.satz' },
  { name: 'Paket',     code: 'XPK', groesse: 'gebinde', schluessel: 'einheit.paket' },
  { name: 'Karton',    code: 'XCT', groesse: 'gebinde', schluessel: 'einheit.karton' },
  { name: 'Palette',   code: 'XPX', groesse: 'gebinde', schluessel: 'einheit.palette' },
  { name: 'Rolle',     code: 'XRO', groesse: 'gebinde', schluessel: 'einheit.rolle' },
  { name: 'Flasche',   code: 'XBO', groesse: 'gebinde', schluessel: 'einheit.flasche' },
  { name: 'Dose',      code: 'XCA', groesse: 'gebinde', schluessel: 'einheit.dose' },
  { name: 'Beutel',    code: 'XBG', groesse: 'gebinde', schluessel: 'einheit.beutel' },
  { name: 'mg',        code: 'MGM', groesse: 'gewicht', faktor: 0.001 },
  { name: 'g',         code: 'GRM', groesse: 'gewicht', faktor: 1 },
  { name: 'kg',        code: 'KGM', groesse: 'gewicht', faktor: 1000 },
  { name: 't',         code: 'TNE', groesse: 'gewicht', faktor: 1_000_000 },
  { name: 'ml',        code: 'MLT', groesse: 'volumen', faktor: 1 },
  { name: 'cl',        code: 'CLT', groesse: 'volumen', faktor: 10 },
  { name: 'l',         code: 'LTR', groesse: 'volumen', faktor: 1000 },
  { name: 'm³',        code: 'MTQ', groesse: 'volumen', faktor: 1_000_000 },
  { name: 'mm',        code: 'MMT', groesse: 'laenge', faktor: 1 },
  { name: 'cm',        code: 'CMT', groesse: 'laenge', faktor: 10 },
  { name: 'm',         code: 'MTR', groesse: 'laenge', faktor: 1000 },
  { name: 'km',        code: 'KMT', groesse: 'laenge', faktor: 1_000_000 },
  { name: 'cm²',       code: 'CMK', groesse: 'flaeche', faktor: 1 },
  { name: 'm²',        code: 'MTK', groesse: 'flaeche', faktor: 10_000 },
  { name: 'Min.',      code: 'MIN', groesse: 'zeit', faktor: 1, schluessel: 'einheit.minute' },
  { name: 'Std.',      code: 'HUR', groesse: 'zeit', faktor: 60, schluessel: 'einheit.stunde' },
  { name: 'Tag',       code: 'DAY', groesse: 'zeit', faktor: 1440, schluessel: 'einheit.tag' },
  { name: 'Woche',     code: 'WEE', groesse: 'zeit', faktor: 10080, schluessel: 'einheit.woche' },
  { name: 'Monat',     code: 'MON', groesse: 'zeit', schluessel: 'einheit.monat' },
  { name: 'Jahr',      code: 'ANN', groesse: 'zeit', schluessel: 'einheit.jahr' },
  { name: 'kWh',       code: 'KWH', groesse: 'energie' },
  { name: 'Pauschale', code: 'LS',  groesse: 'pauschal', schluessel: 'einheit.pauschale' },
];

/** Reihenfolge und Überschriften der Gruppen in der Auswahl. */
export const GRUPPEN: Array<{ groesse: Groesse[]; schluessel: Schluessel }> = [
  { groesse: ['anzahl', 'gebinde'], schluessel: 'einheit.gruppe.anzahl' },
  { groesse: ['gewicht'],           schluessel: 'einheit.gruppe.gewicht' },
  { groesse: ['volumen'],           schluessel: 'einheit.gruppe.volumen' },
  { groesse: ['laenge', 'flaeche'], schluessel: 'einheit.gruppe.laenge' },
  { groesse: ['zeit'],              schluessel: 'einheit.gruppe.zeit' },
  { groesse: ['energie', 'pauschal'], schluessel: 'einheit.gruppe.sonstiges' },
];

const NACH_NAME = new Map(EINHEITEN.map(e => [e.name.toLowerCase(), e]));

export function einheitFinden(name: string | null | undefined): Einheit | null {
  return NACH_NAME.get(String(name ?? '').trim().toLowerCase()) ?? null;
}

/** Anzeigename in der aktuellen Sprache — Symbole bleiben, Wörter werden übersetzt. */
export function einheitAnzeige(name: string | null | undefined, t: (s: Schluessel) => string): string {
  const e = einheitFinden(name);
  if (!e) return String(name ?? '');
  return e.schluessel ? t(e.schluessel) : e.name;
}

/** Gleiche Rechnung wie im Backend — null heißt: nicht umrechenbar. */
export function umrechnen(menge: number, von: string, nach: string): number | null {
  const a = einheitFinden(von);
  const b = einheitFinden(nach);
  if (String(von).trim().toLowerCase() === String(nach).trim().toLowerCase()) return menge;
  if (!a?.faktor || !b?.faktor || a.groesse !== b.groesse) return null;
  return Math.round(menge * (a.faktor / b.faktor) * 1000) / 1000;
}

/** Einheiten, bei denen Kommamengen normal sind — Gewicht, Volumen, Länge, Zeit. */
export function istTeilbar(name: string | null | undefined): boolean {
  const e = einheitFinden(name);
  return !!e && ['gewicht', 'volumen', 'laenge', 'flaeche', 'zeit', 'energie'].includes(e.groesse);
}
